import React from 'react'
import { AlertCircle, Trash2 } from 'react-feather'

import { LoadingSpinner } from '@frame/ui-components/loading-spinner'
import { Button } from '@frame/ui-primitives/button'
import { Span } from '@frame/ui-primitives/span'

import { CircleCheckFull } from '@frame/images/icons/circle-check-full'
import { cn } from '@frame/lib/utils/cn'
import { DataAttribute } from '@frame/types'

import { useDropzoneContext } from '../contexts/dropzone-context'
import { FileStatus, FileStatusMap, FileUploadState } from '../types'

export function getUploadStateTextColor(uploadState: FileUploadState): string {
  switch (uploadState) {
    case FileUploadState.PROCESSING:
      return 'text-secondary text-opacity-50'
    case FileUploadState.FAILED:
      return 'text-error'
    case FileUploadState.COMPLETED:
      return 'text-mint-600'
    default:
      return ''
  }
}

export function getFileStatus(fileStatusMap: FileStatusMap, fileName: string): FileStatus | undefined {
  for (const fileStatus in fileStatusMap) {
    if (fileStatusMap[fileStatus].fileName === fileName) {
      return fileStatusMap[fileStatus]
    }
  }
  return undefined
}

function UploadStateIcon({ uploadState, error }: { uploadState: FileUploadState; error?: string }) {
  const { setErrorMessages } = useDropzoneContext()

  switch (uploadState) {
    case FileUploadState.PROCESSING:
      return (
        <LoadingSpinner
          data-cy={DataAttribute.FILE_UPLOAD_STATE_ICON_LOADING}
          className="text-primary h-[20px] w-[20px]"
        />
      )
    case FileUploadState.FAILED:
      return (
        <AlertCircle
          data-cy={DataAttribute.FILE_UPLOAD_STATE_ICON_ERROR}
          width={20}
          height={20}
          className="cursor-pointer"
          onClick={() => (error ? setErrorMessages([error]) : undefined)}
        />
      )
    case FileUploadState.COMPLETED:
      return (
        <CircleCheckFull data-cy={DataAttribute.FILE_UPLOAD_STATE_ICON_SUCCESS} width={20} height={20} fill="#02816D" />
      )
    default:
      return null
  }
}

type Props = {
  files: File[]
  fileStatusMap: FileStatusMap
  removeFile: (file: File) => void
}

export function Files({ files, fileStatusMap, removeFile }: Props) {
  return (
    <ul data-cy={DataAttribute.DOCUMENTS_LIST}>
      {files.map((file, i) => {
        const fileStatus = getFileStatus(fileStatusMap, file.name)
        const uploadState = fileStatus?.state || FileUploadState.COMPLETED
        const textColor = getUploadStateTextColor(uploadState)
        const canDelete = uploadState !== FileUploadState.PROCESSING

        return (
          <li
            key={file.name}
            className={cn(
              'flex w-full items-center justify-between py-4',
              i !== files.length - 1 && 'border-b border-midnight-200'
            )}
          >
            <div className={cn(textColor, canDelete ? 'w-[calc(100%-24px)]' : 'w-full', 'flex items-center')}>
              <UploadStateIcon uploadState={uploadState} error={fileStatus?.error} />
              <div className="ml-2 w-full truncate">
                <Span className={cn(textColor)}>{file.name}</Span>
              </div>
            </div>
            {canDelete && (
              <Button
                data-cy={DataAttribute.BTN_REMOVE_FILE}
                border={false}
                intent="secondary"
                onClick={() => removeFile(file)}
                className="justify-end"
              >
                <Trash2 size={20} className="mb-[0.5px]" />
              </Button>
            )}
          </li>
        )
      })}
    </ul>
  )
}
